import { dom } from '@omegagrid/core';
import { Form, FormContainer } from '@omegagrid/form';
import { ControlEvent } from '@omegagrid/form/dist/ui/controls/baseControl';
import rp, {
	AppComponent, ParsedComponentId, SqlEditor, Application,
	SqlEditorEvent, SqlEditorResult, MarkdownEditor, GridEditor
} from '@reporting/rp-app-host';
import { Task, Thread } from '@reporting/rp-types/deployment';
import { ThreadFormAdapter, ThreadSqlEditorAdapter, DocEditorAdapter, TaskGridEditorAdapter, TaskFormAdapter } from './adapters';
import { Sidebar } from './components';

const createMainSidebar = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<Sidebar>('rp-mf-deployment-sidebar');
}

const createThreadForm = async (app: Application, componentId: ParsedComponentId) => {
	const idThread = componentId.ids[1] ? parseInt(componentId.ids[1]) : null;
	const duplicate = componentId.ids[2] === 'copy';

	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new ThreadFormAdapter(idThread, duplicate);
	component.addEventListener('form.save', () => (rp.host.components.get('deployment') as Sidebar)?.refresh());
	return component as unknown as AppComponent;
}

const createThreadEditor = async (app: Application, componentId: ParsedComponentId) => {
	const id = parseInt(componentId.ids[1] || "0");
	const thread = id != 0 ? await rp.ms('deployment').get<Thread>(`threads/${id}`, {
		fields: ['id_thread', 'description']
	}) : {
		id_thread: 0,
		description: 'New thread'
	};
	if (!thread) return null;

	return createThreadSqlEditor(app, componentId, thread);
}

const createSqlEditorBeforeExecHandler = (component: AppComponent, app: Application, componentId: ParsedComponentId) => {
	return () => {
		const resultId = `${componentId.type}-threadResult-${componentId.ids[1]}`;
		if (app.host.components.has(resultId)) {
			(app.host.components.components.get(resultId) as SqlEditorResult).clear();
		}
	}
}

const createThreadSqlEditor = (app: Application, componentId: ParsedComponentId, thread: Thread) => {
	const component = dom.createElement<SqlEditor>('rp-editor-sql');
	component.adapter = new ThreadSqlEditorAdapter(thread);
	component.addEventListener('sqleditor.beforeExec', createSqlEditorBeforeExecHandler(component, app, componentId));
	component.addEventListener('sqleditor.result', createSqlEditorResultHandler(component, app, componentId));
	return component;
}

const createSqlEditorResultHandler = (component: AppComponent, app: Application, componentId: ParsedComponentId) => {
	// datasets and messages in stream order - a warning belongs in front of the dataset it preceded
	const results: SqlEditorEvent[] = [];
	let resultPromise: Promise<SqlEditorResult> = null;
	const tabColor = rp.settings.get('result.tab.color') === '0' ? null : rp.settings.get('result.tab.color');

	return async (e: SqlEditorEvent) => {
		if (e.dataset || e.message) {
			results.push(e);
			const resultId = `${componentId.type}-threadResult-${componentId.ids[1]}`;
			const openOptions = { title: `${component.name ?? ''} [result]`.trimStart(), color: tabColor, persistent: false };
			let editorResult = app.host.components?.components.get(resultId) as SqlEditorResult;
			if (editorResult) {
				app.host.open(resultId, openOptions);
			} else {
				if (!resultPromise || !app.host.components?.has(resultId)) {
					resultPromise = app.host.open(resultId, openOptions) as Promise<SqlEditorResult>;
				}
				editorResult = await resultPromise;
			}
			if (!editorResult) return;

			let result: SqlEditorEvent;
			while (result = results.shift()) {
				if (result.dataset) {
					if (result.dataset.index == 0) editorResult.clear();
					editorResult.addDataset(result.dataset);
				} else {
					editorResult.addMessage(result.message);
				}
			}
		}
	}
}

const createSqlEditorResult = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<SqlEditorResult>('rp-editor-sql-result');
}

const createDocEditor = async (app: Application, componentId: ParsedComponentId) => {
	const idThread = componentId.ids[1];
	const thread = await rp.ms('deployment').get<Thread>(`threads/${idThread}`);
	if (!thread) return null;
	const component = dom.createElement<MarkdownEditor>('rp-markdown-editor');
	component.adapter = new DocEditorAdapter(thread);
	return component;
}

const createTasksGrid = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new TaskGridEditorAdapter();
	return component;
}

// deployment-taskForm (new) | deployment-taskForm-{id_task} (edit) | deployment-taskForm-{id_task}-copy (duplicate)
const createTaskForm = async (app: Application, componentId: ParsedComponentId) => {
	const idTask = componentId.ids[1] ? parseInt(componentId.ids[1]) : null;
	const copy = componentId.ids[2] === 'copy';
	let task: Task = null;
	if (idTask) {
		task = await rp.ms('deployment').get<Task>(`tasks/${idTask}`);
		if (copy) {
			task.id_task = null;
			task.idz = null;
		}
	}
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new TaskFormAdapter(task);
	component.addEventListener('change', (e: ControlEvent<Form>) => {
		// the frequency and the sub-day type decide which interval controls the form shows
		if (e.model.props.key === 'freq_type' || e.model.props.key === 'freq_subday_type') {
			(component.adapter as TaskFormAdapter).updateFormControls(component.forms[0]);
		}
	});
	component.addEventListener('form.save', () => {
		try {
			(app.host.components.get('deployment-tasks') as GridEditor).refresh();
		} catch {}
	});
	return component as unknown as AppComponent;
}

export const componentFactories = new Map<string, (app: Application, componentId: ParsedComponentId) => AppComponent|Promise<AppComponent>>([
	['deployment', createMainSidebar],
	['threadForm', createThreadForm],
	['thread', createThreadEditor],
	['threadResult', createSqlEditorResult],
	['docEditor', createDocEditor],
	['tasks', createTasksGrid],
	['taskForm', createTaskForm],
]);
